<template>
  <v-row justify="center">
    <v-dialog
      v-model="dialog_"
      persistent
      scrollable
      max-width="530px"
    >
      <v-card>
        <v-toolbar color="primary" dark class="pl-6">
          <span class="text-h6 text-center">
            Selecione os campos e os ordene como desejado
          </span>
        </v-toolbar>

        <v-card-text style="height: auto">
          <draggable
            tag="div"
            :list="items"
            class="list-group"
            handle=".handle"
            item-key="label"
          >
            <v-card
              v-for="element, index in items"
              :key="index"
              class="mx-auto ma-3"
              elevation="2"
            >
              <div class="d-flex align-center justify-center ma-1">
                <v-switch
                  v-model="element.active"
                  class="ml-2 mr-4 flex-grow-0"
                  color="primary"
                  density="compact"
                  hide-details
                  inset
                  :disabled="element.active && element.mandatory"
                />
                <p class="flex-grow-1 font-weight-medium">
                  {{ element.label }}
                </p>
                <v-icon
                  class="handle"
                  style="cursor: pointer"
                  start
                  color="grey-darken-1"
                  icon="fa-regular fa-sort"
                />
              </div>
            </v-card>
          </draggable>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="red darken-1"
            variant="text"
            :disabled="$loader.loading"
            @click="$emit('close')"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="orange darken-1"
            variant="text"
            :disabled="$loader.loading"
            @click="toggleAll"
          >
            <span v-if="items.some(item => !item.active)">Selecionar Todos</span>
            <span v-else>Desselecionar Todos</span>
          </v-btn>
          <v-btn
            color="green darken-1"
            variant="text"
            :disabled="$loader.loading || items.every(item => !item.active)"
            @click="generate"
          >
            Exportar
          </v-btn>
        </v-card-actions>
        <v-progress-linear v-show="$loader.loading" indeterminate color="primary" />
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { type PropType } from 'vue'
import { VueDraggableNext } from 'vue-draggable-next'
import type { ReportProvider, ReportItem } from '@/types/Report'

export default defineComponent({
  components: {
    draggable: VueDraggableNext,
  },
  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    filename: {
      type: String,
      default: 'relatorio',
      required: true,
    },
    provider: {
      type: Function as PropType<ReportProvider>,
      required: true,
    },
    items: {
      type: Array as PropType<ReportItem[]>,
      required: true,
      validator(value: ReportItem[]) {
        const required = ['label', 'field', 'active']

        for (const item of value) {
          if (!required.every(attr => attr in item)) {
            return false
          }
        }

        return true
      },
    },
    filters: {
      type: String,
      required: true,
    },
  },
  emits: [ 'close' ],
  data() {
    return {
      dialog_: this.dialog,
      dragging: false,
    }
  },
  watch: {
    dialog(newValue): void {
      this.dialog_ = newValue
    },
  },
  methods: {
    async generate(): Promise<void> {
      try {
        this.$loader.increment()

        const selected: Record<string, string> = {}

        this.items.forEach((item: ReportItem) => {
          if (item.active) {
            selected[item.label] = item.field
          }
        })

        const response = await this.provider(this.filters, selected)

        const url = window.URL.createObjectURL(
          new Blob(['\ufeff', response.data]),
        )

        const link = document.createElement('a')
        const date = new Date()

        link.href = url

        link.setAttribute(
          'download',
          `${this.filename}_${date.getDate()}_${date.getMonth() + 1
          }_${date.getFullYear()}.csv`,
        )

        document.body.appendChild(link)

        link.click()

        this.$emit('close')
      } catch (error: unknown) {
        this.$emit('close')
        this.$snackbar.fromAxiosResponse(error)
      } finally {
        this.$loader.decrement()
      }
    },
    toggleAll(): void {
      const all = this.items.every(item => item.active)
      this.items.forEach((item) => {
        item.active = !all
      })
    },
  },
})
</script>
